import { motion } from "framer-motion";
import { Mail, Phone, MapPin, ArrowRight } from "lucide-react";
import { COMPANY } from "../data/content";

export default function Contact() {
    const mailtoSubject = encodeURIComponent(
        "Quote request – Luxe Artificial Grass Ltd"
    );
    const mailtoBody = encodeURIComponent(
        "Hi Luxe team,\n\nI'd love a quote for:\n\n• Project type (artificial grass / patio / driveway / fencing):\n• Where I live:\n• Roughly when I'd like to start:\n• A bit more about what I'm planning:\n\nThanks!\n"
    );
    const mailtoLink = `mailto:${COMPANY.email}?subject=${mailtoSubject}&body=${mailtoBody}`;

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="relative bg-haas-cream py-24 sm:py-32 overflow-hidden"
        >
            <div className="pointer-events-none absolute -top-20 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-haas-sun/30 blur-3xl" />

            <div className="relative max-w-7xl mx-auto px-5 sm:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.7 }}
                    className="text-center max-w-4xl mx-auto mb-14"
                >
                    <span className="chip">👋 Say hello</span>
                    <h2
                        data-testid="contact-headline"
                        className="mt-6 font-display font-bold text-5xl sm:text-7xl lg:text-8xl tracking-tight text-haas-ink leading-[0.98]"
                    >
                        Got a project?{" "}
                        <span className="font-hand text-haas-goldDeep -rotate-2 inline-block">
                            Let's chat.
                        </span>
                    </h2>
                    <p className="mt-6 text-haas-soft text-lg leading-relaxed max-w-2xl mx-auto">
                        Drop us an email or pick up the phone — whichever feels easiest. We'll come out, take a look, and send you an honest written quote within a week. No fee, no pressure, no waffle.
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    {/* Email card */}
                    <motion.a
                        href={mailtoLink}
                        data-testid="contact-email-link"
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="lg:col-span-7 hard-card group p-8 sm:p-10 block bg-white"
                    >
                        <div className="flex items-start justify-between gap-6 mb-6">
                            <div className="w-14 h-14 rounded-2xl bg-haas-sun border-2 border-haas-ink flex items-center justify-center shadow-[3px_3px_0_#123D28]">
                                <Mail className="w-6 h-6 text-haas-ink" />
                            </div>
                            <ArrowRight className="w-6 h-6 text-haas-ink group-hover:translate-x-2 transition-transform" />
                        </div>
                        <div className="text-xs uppercase tracking-[0.22em] text-haas-goldDeep font-bold mb-3">
                            Email — pre-filled & ready
                        </div>
                        <div className="font-display font-bold text-2xl sm:text-3xl text-haas-ink break-all leading-tight">
                            {COMPANY.email}
                        </div>
                        <p className="mt-5 text-haas-soft text-sm">
                            Tap it — we've already started the message for you. Just add your project details.
                        </p>
                    </motion.a>

                    {/* Phone card */}
                    <motion.a
                        href={`tel:${COMPANY.phoneTel}`}
                        data-testid="contact-phone-link"
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="lg:col-span-5 group p-8 sm:p-10 block bg-haas-gold text-haas-ink hover:bg-haas-goldDeep border-2 border-haas-ink rounded-3xl shadow-[8px_8px_0_#123D28] transition-colors"
                    >
                        <div className="flex items-start justify-between gap-6 mb-6">
                            <div className="w-14 h-14 rounded-2xl bg-haas-ink border-2 border-haas-ink flex items-center justify-center shadow-[3px_3px_0_#123D28]">
                                <Phone className="w-6 h-6 text-haas-gold" />
                            </div>
                            <ArrowRight className="w-6 h-6 text-haas-ink group-hover:translate-x-2 transition-transform" />
                        </div>
                        <div className="text-xs uppercase tracking-[0.22em] text-haas-ink/70 font-bold mb-3">
                            Or just give us a bell
                        </div>
                        <div className="font-display font-bold text-3xl sm:text-4xl text-haas-ink leading-tight tracking-tight">
                            {COMPANY.phone}
                        </div>
                        <p className="mt-5 text-haas-ink/75 text-sm">
                            Monday to Friday · 9:00am – 5:00pm. We pick up.
                        </p>
                    </motion.a>

                    {/* Area card */}
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                        data-testid="contact-area-card"
                        className="lg:col-span-12 rounded-3xl bg-white border-2 border-haas-ink shadow-[8px_8px_0_#123D28] p-7 sm:p-9 flex flex-col md:flex-row md:items-center md:justify-between gap-5"
                    >
                        <div className="flex items-center gap-5">
                            <div className="w-12 h-12 rounded-xl bg-haas-coral/20 border-2 border-haas-ink flex items-center justify-center flex-shrink-0">
                                <MapPin className="w-5 h-5 text-haas-ink" />
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-[0.22em] text-haas-goldDeep font-bold mb-1">
                                    Where we work
                                </div>
                                <div
                                    className="font-display font-bold text-2xl sm:text-3xl text-haas-ink leading-snug"
                                    data-testid="contact-area-text"
                                >
                                    West Midlands{" "}
                                    <span className="text-haas-goldDeep">
                                        &amp; surrounding areas
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="text-haas-soft text-sm md:text-right md:max-w-xs">
                            A bit further out? Drop us a line — we'll always have a chat.
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
